/**
 * /signup — the invitation-acceptance page, and nothing else.
 *
 * WHY THIS ROUTE AND NOT A NEW ONE. `/signup` has existed since 2026-05-18 as a
 * one-line redirect to /login, because the founder removed self-signup. It is
 * also the only PUBLIC page in the app whose subject is "a person who does not
 * have an account yet" — which is exactly who opens an invitation link. Reusing
 * it means the invitation lane adds no entry to `routeTree.gen.ts`, a generated
 * file three other agents are editing around today, and it keeps one door for
 * one idea instead of two doors that differ by a word.
 *
 * WITHOUT A TOKEN THE BEHAVIOUR IS BYTE-IDENTICAL TO BEFORE: redirect to
 * /login. Self-signup stays off; `SELF_SIGNUP_ENABLED` is untouched, and so is
 * the boot interlock that refuses to start if it is ever turned on while
 * `ACCOUNT_SCOPED_SUPERADMIN` is off. An invitation is not self-signup — the
 * account it creates was authorised, by name, by an admin of one Workspace,
 * before the person ever arrived.
 *
 * `validateSearch` lives here (the non-lazy half) because TanStack resolves
 * search params before the lazy component loads. The token is a bearer
 * capability in a URL: it is never logged, never put in a page title, and the
 * page replaces it out of the address bar the moment it is spent.
 */
import { createFileRoute, redirect } from '@tanstack/react-router';

/**
 * True once an invitation has been accepted IN THIS TAB.
 *
 * The accept screen spends the token out of the address bar the moment it is
 * used — no history entry, no screenshot, no referrer. `history.replaceState`
 * is observed by TanStack's history, so `beforeLoad` re-runs with an empty
 * search and cannot tell "arrived with no token" (redirect, correct) from
 * "just spent the token that brought them here" (stay, and show them they are
 * in). Measured on 2026-08-05 against the live gateway: the invitation was
 * ACCEPTED (`invitation accepted`, userCreated: true, HTTP 200) and the page
 * then threw its own confirmation away, so the last thing the person saw was a
 * sign-in form with nothing anywhere saying the password they had just chosen
 * was the one to type into it.
 *
 * A module-scope boolean rather than router state or a search param on
 * purpose: it must NOT survive a reload or a new tab. Somebody who reloads
 * /signup with no token has arrived without one, and belongs on /login.
 */
let acceptedInThisTab = false;

/** Called by the accept screen the instant the gateway confirms the join. */
export function markInvitationAccepted(): void {
  acceptedInThisTab = true;
}

export const Route = createFileRoute('/signup')({
  validateSearch: (search: Record<string, unknown>): { token?: string } => {
    const token = typeof search.token === 'string' ? search.token.trim() : '';
    return token ? { token } : {};
  },
  beforeLoad: ({ search }) => {
    // No token → what this route has always done, UNLESS the token was spent
    // here a moment ago and the confirmation panel is what we are standing on.
    if (!search.token && !acceptedInThisTab) throw redirect({ to: '/login' });
  },
}).lazy(() => import('./signup.lazy').then((d) => d.Route));
